import React,{useEffect,useMemo,useRef,useState} from 'react';
import {
  FiArrowRight,
  FiBarChart2,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiCreditCard,
  FiDollarSign,
  FiFileText,
  FiHome,
  FiInfo,
  FiMapPin,
  FiRefreshCw,
  FiUsers
} from 'react-icons/fi';
import SmartDateInput from '../ui/SmartDateInput';
import {calculations} from '../../utils/calculations';
import {rentScheduleService} from '../../services/rentScheduleService';

const STEP_DEFINITIONS=[
  {label:'Property',title:'Property details',subtitle:'Address and basic information'},
  {label:'Purchase',title:'Purchase information',subtitle:'Purchase and ownership'},
  {label:'Finance',title:'Finance and valuation',subtitle:'Loan details'},
  {label:'Review',title:'Review and confirm',subtitle:'Check and confirm'}
];
const propertyTypes=['House','Apartment','Unit Complex','Commercial Office','Industrial Warehouse','Townhouse','Duplex','Villa','Granny Flat','Land','Other'];
const statuses=['Active','Draft','Under Contract','Settled','Renovating','Vacant','Sold','Archived'];
const states=['NSW','VIC','QLD','WA','SA','TAS','ACT','NT'];
const baseState={
  name:'',address:'',suburb:'',state:'NSW',postcode:'',propertyType:'House',propertyUse:'investment',status:'Active',
  acquisitionPrice:0,currentMarketValue:0,acquisitionDate:'',settlementDate:'',ownershipPercent:100,
  stampDuty:0,legalFees:0,buyerAgentFees:0,bankSetupCosts:0,buildingPestCosts:0,initialRepairs:0,otherAcquisitionCosts:0,
  depositAmount:0,depositPercent:20,
  originalLoanAmount:0,currentLoanBalance:0,lenderName:'',loanAccountName:'',loanType:'Investment Loan',
  repaymentType:'Interest Only',interestRate:0,loanTermYears:0,loanStartDate:'',
  landValue:0,buildingValue:0,valuationDate:'',usableEquityLvrPercent:80,financialNotes:'',
  agentAgencyName:'',agentManager:'',agentEmail:'',agentPhone:'',agentEmergencyPhone:'',agentStatementEmail:'',
  agentOfficeAddress:'',managementFeePercent:0,agentEffectiveFrom:'',agentEffectiveTo:'',
  lettingFeeNotes:'',renewalFeeNotes:'',inspectionFeeNotes:'',agentNotes:'',
  rentPreviewAmount:0,rentPreviewBasis:'weekly',otherOngoingChargeAmount:0,otherOngoingChargeBasis:'monthly'
};
const cx=(...classes)=> classes.filter(Boolean).join(' ');
const money=(value)=> calculations.formatCurrencyAUD(Number(value)||0);
const hasText=(value)=> String(value??'').trim()!=='';

const PropertyForm=({initialData,onSubmit,onCancel})=>{
  const [step,setStep]=useState(0);
  const [data,setData]=useState(baseState);
  const [errors,setErrors]=useState({});
  const [moreCostsOpen,setMoreCostsOpen]=useState(false);
  const [agentMore,setAgentMore]=useState(false);
  const [hasLoan,setHasLoan]=useState(false);
  const [hasAgent,setHasAgent]=useState(false);
  const [saving,setSaving]=useState(false);
  const [saveState,setSaveState]=useState(null);
  const loadedRef=useRef(null);
  const dirtyRef=useRef(false);
  const depositMode=useRef('percent');

  useEffect(()=>{
    if(!initialData)return;
    const id=initialData.id||'new';
    if(loadedRef.current===id&&dirtyRef.current)return;
    loadedRef.current=id;
    dirtyRef.current=false;
    const agent=initialData.metadata?.agent||{};
    const agentFeePreview=initialData.metadata?.agentFeePreview||{};
    const financial=initialData.metadata?.financial||{};
    const propertyUse=initialData.propertyUse||initialData.metadata?.propertyUse||
      (String(initialData.loanType||financial.loanType||'').toLowerCase().includes('owner occupier')?'primary_residence':'investment');
    const next={
      ...baseState,
      ...initialData,
      propertyUse,
      agentAgencyName:initialData.agentAgencyName||agent.agencyName||'',
      agentManager:initialData.agentManager||initialData.agentName||agent.managerName||'',
      agentEmail:initialData.agentEmail||agent.email||'',
      agentPhone:initialData.agentPhone||agent.phone||'',
      agentEmergencyPhone:initialData.agentEmergencyPhone||agent.emergencyPhone||'',
      agentStatementEmail:initialData.agentStatementEmail||agent.statementEmail||'',
      agentOfficeAddress:initialData.agentOfficeAddress||agent.officeAddress||'',
      managementFeePercent:initialData.managementFeePercent??agent.managementFeePercent??0,
      agentEffectiveFrom:initialData.agentEffectiveFrom||agent.effectiveFrom||'',
      agentEffectiveTo:initialData.agentEffectiveTo||agent.effectiveTo||'',
      lettingFeeNotes:initialData.lettingFeeNotes||agent.lettingFeeNotes||'',
      renewalFeeNotes:initialData.renewalFeeNotes||agent.renewalFeeNotes||'',
      inspectionFeeNotes:initialData.inspectionFeeNotes||agent.inspectionFeeNotes||'',
      agentNotes:initialData.agentNotes||agent.notes||'',
      rentPreviewAmount:initialData.rentPreviewAmount??agentFeePreview.rentAmount??0,
      rentPreviewBasis:initialData.rentPreviewBasis||agentFeePreview.rentBasis||'weekly',
      otherOngoingChargeAmount:initialData.otherOngoingChargeAmount??agentFeePreview.otherOngoingChargeAmount??0,
      otherOngoingChargeBasis:initialData.otherOngoingChargeBasis||agentFeePreview.otherOngoingChargeBasis||'monthly',
      depositAmount:initialData.depositAmount??financial.depositAmount??0,
      depositPercent:initialData.depositPercent??financial.depositPercent??20,
      originalLoanAmount:initialData.originalLoanAmount??financial.originalLoanAmount??0,
      currentLoanBalance:initialData.currentLoanBalance??financial.currentLoanBalance??0,
      lenderName:initialData.lenderName||financial.lenderName||'',
      loanAccountName:initialData.loanAccountName||financial.loanAccountName||'',
      loanType:initialData.loanType||financial.loanType||'Investment Loan',
      repaymentType:initialData.repaymentType||financial.repaymentType||'Interest Only',
      interestRate:initialData.interestRate??financial.interestRate??0,
      loanTermYears:initialData.loanTermYears??financial.loanTermYears??0,
      loanStartDate:initialData.loanStartDate||financial.loanStartDate||'',
      landValue:initialData.landValue??financial.landValue??0,
      buildingValue:initialData.buildingValue??financial.buildingValue??0,
      valuationDate:initialData.valuationDate||financial.valuationDate||'',
      usableEquityLvrPercent:initialData.usableEquityLvrPercent??financial.usableEquityLvrPercent??80,
      financialNotes:initialData.financialNotes||financial.financialNotes||''
    };
    setData(next);
    setHasLoan(Boolean(Number(next.currentLoanBalance)||Number(next.originalLoanAmount)||next.lenderName||Number(next.interestRate)));
    setHasAgent(Boolean(next.agentAgencyName||next.agentManager||next.agentEmail||next.agentPhone||next.agentStatementEmail||Number(next.managementFeePercent)));
  },[initialData]);

  const linked=useMemo(()=>{
    if(!initialData?.id)return null;
    try{return calculations.getPropertyLoanMetrics(initialData.id);}catch{return null;}
  },[initialData?.id]);
  const linkedLoans=linked?.source==='linked_loans';
  const loanMode=linkedLoans||hasLoan;
  const finalCreateComplete=Boolean(!initialData?.id&&saveState?.success&&!saveState?.draft);

  const markDirty=()=>{
    if(finalCreateComplete)return false;
    dirtyRef.current=true;
    if(saveState)setSaveState(null);
    return true;
  };

  const change=(event)=>{
    if(!markDirty())return;
    const {name,value,type}=event.target;
    const nextValue=type==='number'?Number(value):value;
    setData(previous=>{
      const next={...previous,[name]:nextValue};
      const price=name==='acquisitionPrice'?Number(nextValue||0):Number(previous.acquisitionPrice||0);
      if(name==='depositAmount'){
        depositMode.current='amount';
        next.depositPercent=price>0?Math.round((Number(nextValue||0)/price)*10000)/100:0;
      }
      if(name==='depositPercent'){
        depositMode.current='percent';
        next.depositAmount=price>0?calculations.roundMoney(price*(Number(nextValue||0)/100)):0;
      }
      if(name==='acquisitionPrice'){
        if(price<=0){
          if(depositMode.current==='amount')next.depositPercent=0;
          else next.depositAmount=0;
        }else if(depositMode.current==='amount'){
          next.depositPercent=Math.round((Number(previous.depositAmount||0)/price)*10000)/100;
        }else{
          next.depositAmount=calculations.roundMoney(price*(Number(previous.depositPercent||0)/100));
        }
      }
      return next;
    });
    setErrors(previous=>({...previous,[name]:''}));
  };

  const validate=(names)=>{
    const nextErrors={};
    names.forEach(name=>{
      if(data[name]===null||data[name]===undefined||String(data[name]).trim()==='')nextErrors[name]='Required';
    });
    if(data.name&&data.name.trim().length<2)nextErrors.name='Enter at least 2 characters';
    if(data.address&&data.address.trim().length<5)nextErrors.address='Enter a street address';
    if(data.postcode&&!/^\d{4}$/.test(data.postcode))nextErrors.postcode='Use 4 digits';
    if(Number(data.acquisitionPrice)<0)nextErrors.acquisitionPrice='Cannot be negative';
    if(Number(data.currentMarketValue)<0)nextErrors.currentMarketValue='Cannot be negative';
    setErrors(previous=>({...previous,...nextErrors}));
    return Object.keys(nextErrors).length===0;
  };

  const goNext=()=>{
    const valid=step===0
      ?validate(['name','address','suburb','state','postcode','propertyType','propertyUse','status'])
      :step===1
        ?validate(['acquisitionPrice'])
        :validate(['currentMarketValue']);
    if(valid)setStep(current=>Math.min(3,current+1));
  };

  const buildPayload=(draft=false)=>{
    const finance=loanMode?{
      originalLoanAmount:Number(data.originalLoanAmount)||0,
      currentLoanBalance:Number(data.currentLoanBalance)||0,
      lenderName:data.lenderName||'',
      loanAccountName:data.loanAccountName||'',
      loanType:data.loanType||'',
      repaymentType:data.repaymentType||'',
      interestRate:Number(data.interestRate)||0,
      loanTermYears:Number(data.loanTermYears)||0,
      loanStartDate:data.loanStartDate||''
    }:{
      originalLoanAmount:0,currentLoanBalance:0,lenderName:'',loanAccountName:'',loanType:'',
      repaymentType:'',interestRate:0,loanTermYears:0,loanStartDate:''
    };
    const clearedAgent=hasAgent?{}:{
      agentAgencyName:'',agentManager:'',agentEmail:'',agentPhone:'',agentEmergencyPhone:'',
      agentStatementEmail:'',agentOfficeAddress:'',managementFeePercent:0,agentEffectiveFrom:'',
      agentEffectiveTo:'',lettingFeeNotes:'',renewalFeeNotes:'',inspectionFeeNotes:'',agentNotes:''
    };
    return {
      ...data,
      ...finance,
      ...clearedAgent,
      status:draft?'Draft':data.status,
      metadata:{
        ...(data.metadata||{}),
        propertyUse:data.propertyUse,
        agentFeePreview:hasAgent?{
          rentAmount:Number(data.rentPreviewAmount)||0,
          rentBasis:data.rentPreviewBasis||'weekly',
          otherOngoingChargeAmount:Number(data.otherOngoingChargeAmount)||0,
          otherOngoingChargeBasis:data.otherOngoingChargeBasis||'monthly'
        }:{
          rentAmount:0,
          rentBasis:'weekly',
          otherOngoingChargeAmount:0,
          otherOngoingChargeBasis:'monthly'
        },
        financial:{
          depositAmount:Number(data.depositAmount)||0,
          depositPercent:Number(data.depositPercent)||0,
          ...finance,
          landValue:Number(data.landValue)||0,
          buildingValue:Number(data.buildingValue)||0,
          valuationDate:data.valuationDate||'',
          usableEquityLvrPercent:Number(data.usableEquityLvrPercent)||80,
          financialNotes:data.financialNotes||''
        }
      }
    };
  };

  const save=async(draft=false)=>{
    if(finalCreateComplete)return;
    if(!draft&&!validate(['name','address','suburb','postcode','acquisitionPrice','currentMarketValue','propertyUse']))return;
    setSaving(true);
    try{
      const result=await onSubmit(buildPayload(draft));
      if(result?.success===false)return;
      dirtyRef.current=false;
      setSaveState({
        success:true,
        draft,
        mode:result?.mode||'saved',
        label:draft?'Draft saved':'Saved successfully'
      });
    }finally{
      setSaving(false);
    }
  };

  const costs=calculations.roundMoney(
    ['stampDuty','legalFees','buyerAgentFees','bankSetupCosts','buildingPestCosts','initialRepairs','otherAcquisitionCosts']
      .reduce((sum,key)=>sum+(Number(data[key])||0),0)
  );
  const costBase=calculations.calculatePropertyAcquisitionCost(data);
  const balance=linkedLoans?Number(linked.balance||0):(loanMode?Number(data.currentLoanBalance||0):0);
  const installment=linkedLoans
    ?(linked.installmentComplete?Number(linked.monthlyInstallment||0):null)
    :calculations.calculateLoanMonthlyInstallment({
      balance,
      interestRate:Number(data.interestRate)||0,
      repaymentType:data.repaymentType,
      termYears:Number(data.loanTermYears)||0,
      loanStartDate:data.loanStartDate||null
    });
  const equity=calculations.calculateEquity(Number(data.currentMarketValue)||0,balance);
  const lvr=calculations.calculateLVR(balance,Number(data.currentMarketValue)||0);
  const usable=calculations.roundMoney(
    Math.max(0,(Number(data.currentMarketValue)||0)*(Number(data.usableEquityLvrPercent||0)/100)-balance)
  );

  const rentPreviewFrequencies=[
    {key:'weekly',label:'Weekly'},
    {key:'fortnightly',label:'Fortnightly'},
    {key:'monthly',label:'Monthly'}
  ];
  const rentPreviewRows=rentPreviewFrequencies.map(({key,label})=>{
    const rentCalculation=rentScheduleService.getRentPeriodCalculation(
      Number(data.rentPreviewAmount)||0,
      data.rentPreviewBasis||'weekly',
      key
    );
    const otherChargeCalculation=rentScheduleService.getRentPeriodCalculation(
      Number(data.otherOngoingChargeAmount)||0,
      data.otherOngoingChargeBasis||'monthly',
      key
    );
    const grossRent=rentCalculation.supported?rentCalculation.grossRentPerPeriod:0;
    const commission=rentScheduleService.calculateAgentFee(grossRent,Number(data.managementFeePercent)||0);
    const otherCharges=otherChargeCalculation.supported?otherChargeCalculation.grossRentPerPeriod:0;
    const totalDeductions=rentScheduleService.calculateTotalDeductions(commission,otherCharges);
    const netOwnerAmount=rentScheduleService.calculateNetOwnerAmount(grossRent,totalDeductions);
    return {key,label,grossRent,commission,otherCharges,totalDeductions,netOwnerAmount};
  });
  const hasRentPreview=(Number(data.rentPreviewAmount)||0)>0;

  const requiredFields=['name','address','suburb','state','postcode','propertyType','propertyUse','status'];
  const requiredComplete=requiredFields.filter(name=>hasText(data[name])).length;
  const propertyReady=requiredComplete===requiredFields.length&&/^\d{4}$/.test(String(data.postcode||''));
  const purchaseReady=Number(data.acquisitionPrice)>=0&&hasText(data.acquisitionPrice);
  const financeReady=Number(data.currentMarketValue)>=0&&hasText(data.currentMarketValue);
  const completionCount=[propertyReady,purchaseReady,financeReady,true].filter(Boolean).length;
  const imageUrl=initialData?.propertyImageUrls?.[0]||initialData?.propertyImageThumbUrls?.[0]||initialData?.imageUrl||'';

  const inputClass='w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-200';
  const labelClass='mb-1.5 block text-xs font-semibold text-slate-700';
  const cardClass='rounded-xl border border-slate-200 bg-white shadow-sm';

  const field=(name,label,type='text',options=null,extra={})=>(
    <div className={extra.wrapperClass||''}>
      <label className={labelClass}>{label}</label>
      {options?(
        <select
          name={name}
          value={data[name]}
          onChange={change}
          disabled={finalCreateComplete}
          className={cx(inputClass,errors[name]&&'border-red-300',finalCreateComplete&&'cursor-not-allowed opacity-70')}
        >
          {options.map(option=><option key={option} value={option}>{option}</option>)}
        </select>
      ):type==='date'?(
        <SmartDateInput
          name={name}
          value={data[name]||''}
          onChange={change}
          disabled={finalCreateComplete}
          className={cx(inputClass,errors[name]&&'border-red-300',finalCreateComplete&&'cursor-not-allowed opacity-70')}
          {...(name==='settlementDate'?{baseDate:data.acquisitionDate,baseLabel:'the purchase date'}:{})}
          {...(name==='agentEffectiveTo'?{baseDate:data.agentEffectiveFrom,baseLabel:'the agent effective-from date'}:{})}
        />
      ):(
        <input
          name={name}
          type={type}
          step={type==='number'?'0.01':undefined}
          value={data[name]??''}
          onChange={change}
          disabled={finalCreateComplete}
          className={cx(inputClass,errors[name]&&'border-red-300',finalCreateComplete&&'cursor-not-allowed opacity-70')}
          {...extra.inputProps}
        />
      )}
      {errors[name]&&<p className="mt-1 text-xs font-medium text-red-600">{errors[name]}</p>}
    </div>
  );

  const summaryLine=(label,value,strong=false)=>(
    <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={cx('text-right font-semibold text-slate-800',strong&&'text-base font-bold text-slate-950')}>{value}</span>
    </div>
  );

  const setUse=(value)=>{
    if(!markDirty())return;
    setData(previous=>({...previous,propertyUse:value}));
  };

  const setLoanChoice=(value)=>{
    if(!markDirty())return;
    setHasLoan(value);
  };

  const setAgentChoice=(value)=>{
    if(!markDirty())return;
    setHasAgent(value);
  };

  const stepper=(
    <div className="px-1 pb-2 pt-1 sm:px-4">
      <div className="flex items-start">
        {STEP_DEFINITIONS.map((definition,index)=>(
          <React.Fragment key={definition.label}>
            <button
              type="button"
              onClick={()=>index<=step&&setStep(index)}
              className={cx('min-w-0 flex-1 text-center',index<=step?'cursor-pointer':'cursor-default')}
            >
              <span
                className={cx(
                  'mx-auto flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-bold transition sm:h-10 sm:w-10',
                  index<step&&'border-emerald-600 bg-emerald-600 text-white',
                  index===step&&'border-amber-500 bg-amber-500 text-white shadow-[0_0_0_4px_rgba(245,158,11,0.12)]',
                  index>step&&'border-slate-300 bg-slate-50 text-slate-500'
                )}
              >
                {index<step?<FiCheck className="text-lg"/>:index+1}
              </span>
              <span className={cx('mt-2 block truncate text-[11px] font-bold sm:text-sm',index===step?'text-amber-700':index<step?'text-emerald-700':'text-slate-500')}>
                <span className="hidden md:inline">{index+1}. </span>{definition.label}
              </span>
              <span className="mt-0.5 hidden text-xs text-slate-500 lg:block">{definition.subtitle}</span>
            </button>
            {index<STEP_DEFINITIONS.length-1&&(
              <div className={cx('mt-[18px] h-0.5 min-w-3 flex-[0.45] sm:min-w-8',index<step?'bg-emerald-500':index===step?'bg-amber-400':'bg-slate-200')}/>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const sectionHeader=(number,title,description)=>(
    <div className="mb-5">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600">Step {number} of 4</p>
      <h3 className="mt-1 text-2xl font-bold tracking-tight text-slate-950 sm:text-[28px]">{title}</h3>
      <p className="mt-1 text-sm text-slate-500">{description}</p>
    </div>
  );

  const essentials=(
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_310px]">
      <section className="min-w-0">
        {sectionHeader(1,'Property details','Start with the essentials. You can complete optional details later.')}
        <div className="space-y-4">
          {field('name','Property name or nickname')}
          <div>
            <label className={labelClass}>Street address</label>
            <div className="relative">
              <FiMapPin className="pointer-events-none absolute left-3.5 top-3 text-slate-400"/>
              <input
                name="address"
                value={data.address||''}
                onChange={change}
                disabled={finalCreateComplete}
                placeholder="Enter street address"
                className={cx(inputClass,'pl-10',errors.address&&'border-red-300',finalCreateComplete&&'cursor-not-allowed opacity-70')}
              />
            </div>
            {errors.address&&<p className="mt-1 text-xs font-medium text-red-600">{errors.address}</p>}
            <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-400"><FiInfo/>Manual address entry is enabled. No external address service is connected.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-[1.4fr_0.8fr_0.8fr]">
            {field('suburb','Suburb')}
            {field('state','State','text',states)}
            {field('postcode','Postcode','text',null,{inputProps:{inputMode:'numeric',maxLength:4}})}
          </div>
          {field('propertyType','Property type','text',propertyTypes)}
          <div>
            <label className={labelClass}>Property use</label>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                disabled={finalCreateComplete}
                onClick={()=>setUse('investment')}
                className={cx(
                  'flex min-h-[82px] items-center gap-3 rounded-xl border-2 p-4 text-left transition',
                  data.propertyUse==='investment'?'border-amber-500 bg-amber-50 shadow-sm':'border-slate-200 bg-white hover:border-slate-300',
                  finalCreateComplete&&'cursor-not-allowed opacity-70'
                )}
              >
                <span className={cx('flex h-11 w-11 shrink-0 items-center justify-center rounded-full',data.propertyUse==='investment'?'bg-amber-100 text-amber-700':'bg-slate-100 text-slate-600')}><FiHome className="text-xl"/></span>
                <span className="min-w-0 flex-1">
                  <b className="block text-sm text-slate-900">Investment property</b>
                  <span className="mt-0.5 block text-xs text-slate-500">Generate rental income</span>
                </span>
                <span className={cx('h-5 w-5 shrink-0 rounded-full border-2',data.propertyUse==='investment'?'border-amber-500 bg-amber-500 shadow-[inset_0_0_0_4px_white]':'border-slate-300')}/>
              </button>
              <button
                type="button"
                disabled={finalCreateComplete}
                onClick={()=>setUse('primary_residence')}
                className={cx(
                  'flex min-h-[82px] items-center gap-3 rounded-xl border-2 p-4 text-left transition',
                  data.propertyUse==='primary_residence'?'border-blue-500 bg-blue-50 shadow-sm':'border-slate-200 bg-white hover:border-slate-300',
                  finalCreateComplete&&'cursor-not-allowed opacity-70'
                )}
              >
                <span className={cx('flex h-11 w-11 shrink-0 items-center justify-center rounded-full',data.propertyUse==='primary_residence'?'bg-blue-100 text-blue-700':'bg-slate-100 text-slate-600')}><FiHome className="text-xl"/></span>
                <span className="min-w-0 flex-1">
                  <b className="block text-sm text-slate-900">Primary residence / PPR</b>
                  <span className="mt-0.5 block text-xs text-slate-500">Live in this property</span>
                </span>
                <span className={cx('h-5 w-5 shrink-0 rounded-full border-2',data.propertyUse==='primary_residence'?'border-blue-500 bg-blue-500 shadow-[inset_0_0_0_4px_white]':'border-slate-300')}/>
              </button>
            </div>
          </div>
          {field('status','Status','text',statuses)}
        </div>
      </section>

      <aside className={cx(cardClass,'h-fit overflow-hidden lg:sticky lg:top-0')}>
        <div className="p-5">
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">Property snapshot</p>
          <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
            {imageUrl?(
              <img src={imageUrl} alt="" className="h-36 w-full object-cover"/>
            ):(
              <div className="flex h-36 items-center justify-center bg-gradient-to-br from-slate-100 via-white to-amber-50 text-slate-300">
                <FiHome className="text-5xl"/>
              </div>
            )}
          </div>
          <h4 className="mt-4 truncate text-xl font-bold text-slate-950">{data.name||'New property'}</h4>
          <span className={cx('mt-2 inline-flex rounded-lg px-2.5 py-1 text-xs font-semibold',data.propertyUse==='primary_residence'?'bg-blue-50 text-blue-700':'bg-amber-50 text-amber-700')}>
            {data.propertyUse==='primary_residence'?'Primary residence / PPR':'Investment property'}
          </span>
          <div className="my-5 border-t border-slate-200"/>
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-slate-600">Required fields</span>
            <span className="font-bold text-slate-800">{requiredComplete} of {requiredFields.length}</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{width:String((requiredComplete/requiredFields.length)*100)+'%'}}/>
          </div>
          <div className={cx('mt-5 flex items-center gap-3 rounded-xl border p-4',propertyReady?'border-emerald-200 bg-emerald-50 text-emerald-700':'border-slate-200 bg-slate-50 text-slate-500')}>
            <span className={cx('flex h-8 w-8 items-center justify-center rounded-full',propertyReady?'bg-emerald-600 text-white':'bg-slate-200 text-slate-500')}>
              {propertyReady?<FiCheck/>:<FiInfo/>}
            </span>
            <div>
              <b className="block text-sm">{propertyReady?'Ready to continue':'Complete required fields'}</b>
              <span className="text-xs">{propertyReady?'Property essentials are complete.':'Required fields are highlighted as you continue.'}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );

  const purchase=(
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <section className="min-w-0">
        {sectionHeader(2,'Purchase and ownership','Record purchase, ownership, deposit and acquisition costs.')}
        <div className="grid gap-4 md:grid-cols-3">
          {field('acquisitionPrice','Purchase price ($)','number')}
          {field('acquisitionDate','Purchase date','date')}
          {field('settlementDate','Settlement date','date')}
        </div>
        <div className="mt-4 max-w-xs">{field('ownershipPercent','Ownership (%)','number')}</div>

        <div className="mt-6">
          <label className={labelClass}>Deposit</label>
          <div className="grid items-center gap-3 sm:grid-cols-[minmax(0,1fr)_42px_minmax(0,1fr)]">
            <div>{field('depositAmount','Amount ($)','number')}</div>
            <div className="hidden h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-blue-600 sm:flex"><FiRefreshCw/></div>
            <div>{field('depositPercent','Percentage (%)','number')}</div>
          </div>
          <p className="mt-2 flex items-center gap-2 text-xs text-slate-500"><FiInfo className="text-blue-600"/>Deposit and percentage stay in sync. Changing either field updates the other.</p>
        </div>

        <div className={cx(cardClass,'mt-6 overflow-hidden')}>
          <div className="flex items-center justify-between px-4 py-4 sm:px-5">
            <div>
              <h4 className="text-sm font-bold text-slate-900">Acquisition costs</h4>
              <p className="mt-0.5 text-xs text-slate-500">Capture the costs that form part of the acquisition cost base.</p>
            </div>
            <span className="text-sm font-bold text-slate-700">{money(costs)}</span>
          </div>
          <div className="grid gap-4 border-t border-slate-100 px-4 py-4 sm:grid-cols-3 sm:px-5">
            {field('stampDuty','Stamp duty','number')}
            {field('legalFees','Legal and conveyancing','number')}
            {field('buyerAgentFees','Buyer agent fees','number')}
          </div>
          {moreCostsOpen&&(
            <div className="grid gap-4 border-t border-slate-100 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4 sm:px-5">
              {field('bankSetupCosts','Bank setup','number')}
              {field('buildingPestCosts','Building / pest','number')}
              {field('initialRepairs','Initial repairs / setup','number')}
              {field('otherAcquisitionCosts','Other costs','number')}
            </div>
          )}
          <button
            type="button"
            onClick={()=>setMoreCostsOpen(value=>!value)}
            className="flex items-center gap-2 px-4 pb-4 text-xs font-bold text-blue-700 sm:px-5"
          >
            {moreCostsOpen?'Hide 4 additional costs':'Show 4 more costs'}
            {moreCostsOpen?<FiChevronUp/>:<FiChevronDown/>}
          </button>
        </div>
      </section>

      <aside className={cx(cardClass,'h-fit p-5 lg:sticky lg:top-0')}>
        <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">Purchase summary</p>
        <div className="mt-4 divide-y divide-slate-100">
          {summaryLine('Purchase price',money(data.acquisitionPrice))}
          {summaryLine('Total acquisition costs',money(costs))}
          {summaryLine('Total cost base',money(costBase),true)}
        </div>
        <div className="mt-5 flex items-start gap-3 rounded-xl bg-emerald-50 p-4 text-emerald-800">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100"><FiDollarSign/></span>
          <div>
            <b className="block text-sm">{money(data.acquisitionPrice)} + {money(costs)} = {money(costBase)}</b>
            <span className="mt-1 block text-xs text-emerald-700">Updated in real time as you enter details.</span>
          </div>
        </div>
      </aside>
    </div>
  );

  const previewCard=(label,value,icon,tone,caption='')=>{
    const tones={
      blue:'bg-blue-50 text-blue-900 border-blue-100',
      green:'bg-emerald-50 text-emerald-900 border-emerald-100',
      amber:'bg-amber-50 text-amber-950 border-amber-100',
      indigo:'bg-indigo-50 text-indigo-950 border-indigo-100'
    };
    const iconTones={
      blue:'bg-blue-100 text-blue-700',
      green:'bg-emerald-100 text-emerald-700',
      amber:'bg-amber-100 text-amber-800',
      indigo:'bg-indigo-100 text-indigo-700'
    };
    return (
      <div className={cx('rounded-xl border p-4',tones[tone])}>
        <div className="flex items-start gap-3">
          <span className={cx('flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xl',iconTones[tone])}>{icon}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium opacity-80">{label}</span>
              <FiInfo className="shrink-0 opacity-70"/>
            </div>
            <div className="mt-1 truncate text-2xl font-extrabold tracking-tight">{value}</div>
            {caption&&<div className="mt-1 text-[11px] opacity-70">{caption}</div>}
          </div>
        </div>
      </div>
    );
  };

  const finance=(
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_330px]">
      <section className="min-w-0">
        {sectionHeader(3,'Finance and valuation','Choose whether the property has finance, then confirm current valuation details.')}
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            disabled={linkedLoans||finalCreateComplete}
            onClick={()=>setLoanChoice(false)}
            className={cx(
              'flex min-h-[58px] items-center gap-3 rounded-xl border-2 px-4 text-left transition disabled:opacity-50',
              !loanMode?'border-slate-900 bg-slate-900 text-white':'border-slate-200 bg-white text-slate-800'
            )}
          >
            <span className={cx('h-5 w-5 rounded-full border-2',!loanMode?'border-white bg-white shadow-[inset_0_0_0_4px_#0f172a]':'border-slate-300')}/>
            <span><b className="block text-sm">No loan</b><small className="text-xs opacity-70">No property-level debt</small></span>
          </button>
          <button
            type="button"
            disabled={finalCreateComplete}
            onClick={()=>setLoanChoice(true)}
            className={cx(
              'flex min-h-[58px] items-center gap-3 rounded-xl border-2 px-4 text-left transition',
              loanMode?'border-blue-500 bg-blue-50 text-blue-800':'border-slate-200 bg-white text-slate-800',
              finalCreateComplete&&'cursor-not-allowed opacity-70'
            )}
          >
            <span className={cx('h-5 w-5 rounded-full border-2',loanMode?'border-blue-600 bg-blue-600 shadow-[inset_0_0_0_4px_white]':'border-slate-300')}/>
            <span><b className="block text-sm">Add loan details</b><small className="text-xs opacity-70">Lender, balance, rate and term</small></span>
          </button>
        </div>

        {linkedLoans&&(
          <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs leading-5 text-blue-700">
            Active linked loan records already exist. They remain authoritative and are used for the live preview; this wizard does not replace or double-count them.
          </div>
        )}

        {loanMode&&(
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {field('lenderName','Lender or bank')}
            {field('loanAccountName','Loan account or split')}
            {field('loanType','Loan type','text',['Investment Loan','Owner Occupier Loan','Equity Release','Line of Credit','Construction Loan','Other'])}
            {field('repaymentType','Repayment type','text',['Interest Only','Principal & Interest','Line of Credit','Other'])}
            {field('originalLoanAmount','Original loan ($)','number')}
            {field('currentLoanBalance','Current balance ($)','number')}
            {field('interestRate','Interest rate (%)','number')}
            {field('loanTermYears','Original term (years)','number')}
            {field('loanStartDate','Loan start date','date')}
          </div>
        )}

        <div className={cx('grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2',loanMode&&'mt-6')}>
          {field('currentMarketValue','Current market value ($)','number')}
          {field('valuationDate','Valuation date','date')}
        </div>

        <details className="mt-5 rounded-xl border border-slate-200 bg-white">
          <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">Advanced valuation details</summary>
          <div className="grid gap-4 border-t border-slate-100 p-4 sm:grid-cols-2">
            {field('landValue','Land value ($)','number')}
            {field('buildingValue','Building / improvement value ($)','number')}
            {field('usableEquityLvrPercent','Usable equity LVR limit (%)','number')}
            <div className="sm:col-span-2">
              <label className={labelClass}>Financial notes</label>
              <textarea
                name="financialNotes"
                value={data.financialNotes||''}
                onChange={change}
                rows="3"
                disabled={finalCreateComplete}
                className={cx(inputClass,finalCreateComplete&&'cursor-not-allowed opacity-70')}
              />
            </div>
          </div>
        </details>

        <div className={cx('mt-4 flex items-center gap-2 text-xs font-semibold',financeReady?'text-emerald-700':'text-slate-400')}>
          <FiCheckCircle/>{financeReady?'Finance inputs complete':'Complete the valuation to continue'}
        </div>
      </section>

      <aside className={cx(cardClass,'h-fit p-5 lg:sticky lg:top-0')}>
        <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">Finance preview</p>
        <div className="mt-4 space-y-3">
          {previewCard('Estimated monthly instalment',installment===null?'Incomplete':money(installment),<FiCreditCard/>,'blue')}
          {previewCard('Current equity',money(equity),<FiHome/>,'green')}
          {previewCard('LVR',calculations.formatPercentage(lvr,false)+'%',<FiBarChart2/>,'amber')}
          {previewCard('Usable equity at '+Number(data.usableEquityLvrPercent||0).toFixed(0)+'% LVR',money(usable),<FiBarChart2/>,'indigo')}
        </div>
        <p className="mt-4 text-xs text-slate-400">Calculated from the values entered and any authoritative linked loan records.</p>
      </aside>
    </div>
  );

  const reviewCard=(title,target,icon,children)=>(
    <div className={cx(cardClass,'p-4')}>
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-50 text-xl text-amber-700">{icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">{title}</span>
            <div className="flex items-center gap-3">
              <button type="button" onClick={()=>setStep(target)} className="text-xs font-bold text-blue-700 underline decoration-blue-200 underline-offset-2">Edit</button>
              <FiCheckCircle className="text-xl text-emerald-600"/>
            </div>
          </div>
          <div className="mt-2 text-sm leading-6 text-slate-700">{children}</div>
        </div>
      </div>
    </div>
  );

  const review=(
    <div>
      {sectionHeader(4,'Review and save','Check the important details before saving this property.')}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_390px]">
        <section className="space-y-3">
          {reviewCard('Property',0,<FiHome/>,(
            <>
              <div className="font-bold text-slate-950">{data.name||'-'}</div>
              <div>{data.propertyUse==='primary_residence'?'Primary residence / PPR':'Investment property'}</div>
              <div className="text-slate-500">{[data.address,data.suburb,data.state,data.postcode].filter(Boolean).join(', ')||'-'}</div>
            </>
          ))}
          {reviewCard('Purchase',1,<FiDollarSign/>,(
            <>
              <div><b className="text-slate-950">{money(data.acquisitionPrice)}</b> purchase price</div>
              <div><b className="text-slate-950">{money(costBase)}</b> total cost base</div>
              <div className="text-slate-500">{Number(data.ownershipPercent||0).toFixed(2)}% ownership</div>
            </>
          ))}
          {reviewCard('Finance',2,<FiCreditCard/>,(
            <>
              <div><b className="text-slate-950">{money(balance)}</b> current balance</div>
              <div>{loanMode?(String(Number(data.interestRate||0).toFixed(2))+'% interest'):'No loan'}</div>
              <div><b className="text-slate-950">{installment===null?'Incomplete':money(installment)}</b> estimated monthly instalment</div>
            </>
          ))}
          {reviewCard('Valuation',2,<FiBarChart2/>,(
            <>
              <div><b className="text-slate-950">{money(data.currentMarketValue)}</b> current market value</div>
              <div><b className="text-slate-950">{money(equity)}</b> equity</div>
              <div className="text-slate-500">{data.valuationDate?calculations.formatDateAU(data.valuationDate):'Valuation date not recorded'}</div>
            </>
          ))}
        </section>

        <aside className="space-y-4">
          <div className={cx(cardClass,'p-5')}>
            <h4 className="text-base font-bold text-slate-950">Managed by an agent?</h4>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              {data.propertyUse==='primary_residence'?'No is the normal default for a PPR.':'Add the current managing agent when applicable.'}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={finalCreateComplete}
                onClick={()=>setAgentChoice(true)}
                className={cx('rounded-lg border-2 px-3 py-2.5 text-sm font-bold transition',hasAgent?'border-amber-500 bg-amber-50 text-amber-800':'border-slate-200 bg-white text-slate-700',finalCreateComplete&&'cursor-not-allowed opacity-70')}
              >Yes</button>
              <button
                type="button"
                disabled={finalCreateComplete}
                onClick={()=>setAgentChoice(false)}
                className={cx('rounded-lg border-2 px-3 py-2.5 text-sm font-bold transition',!hasAgent?'border-slate-800 bg-slate-900 text-white':'border-slate-200 bg-white text-slate-700',finalCreateComplete&&'cursor-not-allowed opacity-70')}
              >No</button>
            </div>

            {hasAgent&&(
              <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
                {field('agentAgencyName','Agency')}
                {field('agentManager','Property manager')}
                {field('managementFeePercent','Management fee / commission (%)','number')}
                {field('agentStatementEmail','Statement email','email')}

                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><FiDollarSign/></span>
                    <div>
                      <h5 className="text-sm font-bold text-slate-900">Live agent commission & net rent</h5>
                      <p className="mt-0.5 text-[11px] leading-4 text-slate-500">Planning preview using the same calculation engine as the Rent Schedule. Actual saved rent schedules and transactions remain authoritative.</p>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                    <div>
                      <label className={labelClass}>Expected rent amount ($)</label>
                      <input
                        name="rentPreviewAmount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.rentPreviewAmount??0}
                        onChange={change}
                        disabled={finalCreateComplete}
                        placeholder="450.00"
                        className={cx(inputClass,finalCreateComplete&&'cursor-not-allowed opacity-70')}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Rent basis</label>
                      <select
                        name="rentPreviewBasis"
                        value={data.rentPreviewBasis||'weekly'}
                        onChange={change}
                        disabled={finalCreateComplete}
                        className={cx(inputClass,finalCreateComplete&&'cursor-not-allowed opacity-70')}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="fortnightly">Fortnightly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Other ongoing charges ($)</label>
                      <input
                        name="otherOngoingChargeAmount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={data.otherOngoingChargeAmount??0}
                        onChange={change}
                        disabled={finalCreateComplete}
                        placeholder="0.00"
                        className={cx(inputClass,finalCreateComplete&&'cursor-not-allowed opacity-70')}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Other charge basis</label>
                      <select
                        name="otherOngoingChargeBasis"
                        value={data.otherOngoingChargeBasis||'monthly'}
                        onChange={change}
                        disabled={finalCreateComplete}
                        className={cx(inputClass,finalCreateComplete&&'cursor-not-allowed opacity-70')}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="fortnightly">Fortnightly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                  </div>

                  {hasRentPreview?(
                    <div className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-1">
                      {rentPreviewRows.map(row=>(
                        <div key={row.key} className="rounded-lg border border-emerald-200 bg-white p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{row.label}</span>
                            <span className="text-sm font-extrabold text-emerald-700">{money(row.netOwnerAmount)} net</span>
                          </div>
                          <div className="mt-2 space-y-1 text-[11px]">
                            <div className="flex justify-between gap-3"><span className="text-slate-500">Gross rent</span><span className="font-semibold text-slate-700">{money(row.grossRent)}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-slate-500">Commission ({Number(data.managementFeePercent||0).toFixed(2)}%)</span><span className="font-semibold text-red-600">− {money(row.commission)}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-slate-500">Other ongoing charges</span><span className="font-semibold text-red-600">− {money(row.otherCharges)}</span></div>
                            <div className="flex justify-between gap-3 border-t border-slate-100 pt-1"><span className="font-semibold text-slate-700">Net payable to owner</span><span className="font-bold text-emerald-700">{money(row.netOwnerAmount)}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ):(
                    <div className="mt-4 rounded-lg border border-dashed border-emerald-200 bg-white/70 px-3 py-3 text-xs text-slate-500">Enter the expected rent amount to see live weekly, fortnightly and monthly net-rent calculations.</div>
                  )}

                  {hasRentPreview&&(
                    <div className="mt-3 rounded-lg bg-white/70 px-3 py-2 text-[11px] leading-4 text-slate-500">
                      Formula: gross rent − management commission − converted ongoing charges = net payable to owner. Ongoing charges are annualised from their selected basis before being converted to each displayed period.
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={()=>setAgentMore(value=>!value)}
                  className="flex items-center gap-2 text-xs font-bold text-blue-700"
                >
                  {agentMore?<FiChevronUp/>:<FiChevronDown/>}
                  {agentMore?'Hide optional agent details':'Add more agent details'}
                </button>
                {agentMore&&(
                  <div className="space-y-4 rounded-xl bg-slate-50 p-4">
                    {field('agentEmail','Agent email','email')}
                    {field('agentPhone','Agent phone')}
                    {field('agentEmergencyPhone','Emergency phone')}
                    {field('agentOfficeAddress','Office address')}
                    {field('agentEffectiveFrom','Effective from','date')}
                    {field('agentEffectiveTo','Effective to','date')}
                    {field('lettingFeeNotes','Letting fee notes')}
                    {field('renewalFeeNotes','Renewal fee notes')}
                    {field('inspectionFeeNotes','Inspection fee notes')}
                    <div>
                      <label className={labelClass}>Agent agreement / general notes</label>
                      <textarea
                        name="agentNotes"
                        value={data.agentNotes||''}
                        onChange={change}
                        rows="4"
                        disabled={finalCreateComplete}
                        placeholder="Record negotiated terms, advertising or marketing arrangements, special conditions, or other agreement notes."
                        className={cx(inputClass,finalCreateComplete&&'cursor-not-allowed opacity-70')}
                      />
                      <p className="mt-1 text-[11px] leading-4 text-slate-400">Use this for agreement context. Record actual paid advertising or marketing expenses as property transactions so they remain reportable.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <div className="flex gap-3">
              <FiInfo className="mt-0.5 shrink-0 text-xl text-blue-700"/>
              <div>
                <b className="text-xs uppercase tracking-[0.08em] text-blue-800">Optional details</b>
                <p className="mt-1 text-xs leading-5 text-blue-700">Agent dates, phone numbers, fee notes and agreement notes can be added now or later.</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white"><FiCheck className="text-xl"/></span>
              <div>
                <b className="block text-sm text-emerald-900">{completionCount} sections complete</b>
                <span className="mt-0.5 block text-xs text-emerald-700">{completionCount===4?'All required information is ready.':'Review any incomplete sections before final save.'}</span>
              </div>
            </div>
          </div>

          {saveState?.success&&(
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4">
              <div className="flex gap-3">
                <FiCheckCircle className="mt-0.5 shrink-0 text-2xl text-emerald-700"/>
                <div>
                  <b className="block text-sm text-emerald-900">{saveState.draft?'Draft saved':'Property saved successfully'}</b>
                  <p className="mt-1 text-xs leading-5 text-emerald-700">
                    {saveState.draft?'You can keep working in this wizard.':'This window will stay open until you choose Close, Cancel, or the X button.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );

  const screen=[essentials,purchase,finance,review][step];

  return (
    <div className="min-w-0">
      {stepper}
      <div className="mt-4 border-t border-slate-100 pt-5 sm:mt-5 sm:pt-6">{screen}</div>

      <div className="sticky bottom-0 z-20 -mx-1 mt-6 border-t border-slate-200 bg-white/95 px-1 pb-1 pt-4 backdrop-blur">
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            {step>0&&(
              <button type="button" onClick={()=>setStep(current=>current-1)} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Back
              </button>
            )}
            <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-50">Cancel</button>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {!finalCreateComplete&&(
              <button
                type="button"
                disabled={saving}
                onClick={()=>save(true)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {saving?'Saving...':saveState?.draft?'Draft Saved':'Save as Draft'}
              </button>
            )}

            {step<3?(
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-bold text-slate-950 shadow-sm transition hover:bg-amber-400"
              >
                {step===0?'Continue to Purchase':step===1?'Continue to Finance':'Continue to Review'} <FiArrowRight/>
              </button>
            ):saveState?.success&&!saveState?.draft?(
              <>
                <button type="button" disabled className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white opacity-90"><FiCheckCircle className="mr-2 inline"/>Saved</button>
                <button type="button" onClick={onCancel} className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800">Close</button>
              </>
            ):(
              <button
                type="button"
                disabled={saving}
                onClick={()=>save(false)}
                className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
              >
                {saving?'Saving...':initialData?.id?'Save Changes':'Create Property'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyForm;